/**
 * Personalkosten: Einsatz nachtragen, Stundenzeile bearbeiten, Kostenzelle.
 * Aus pages/ProjektDetailPage.tsx herausgelöst (Plan Projekt-Tracking, Etappe 2) —
 * verschoben, nicht umgeschrieben.
 */
import { useMemo, useState } from "react";
import { useCreateStunde, useTeamMembers, useTeamOwners } from "../../hooks/usePlanung";
import type { StundenZeile } from "../../lib/types";
import { formatEur } from "../../lib/planer";
import { h1Zahl, mitUhrzeit, uhrzeit, zahl } from "./seiteHilfen";

/* ══ Personalkosten: Stundenzeilen ═══════════════════════════════════════ */

export type PersonenFilter = "alle" | "helfer" | "inhaber";

export const FILTER_LABEL: { wert: PersonenFilter; text: string }[] = [
  { wert: "alle", text: "Alle" },
  { wert: "helfer", text: "Helfer" },
  { wert: "inhaber", text: "Inhaber" },
];

/** Heute als YYYY-MM-DD in Ortszeit.
 *
 * Nicht `toISOString()`: das rechnet nach UTC und liefert in Berlin zwischen
 * Mitternacht und 2 Uhr den Vortag — genau die Stunde, in der jemand den
 * Feierabend nachtraegt. */
export function heuteIso(): string {
  const d = new Date();
  const p2 = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

/** Ein Einsatz ist offen, solange er nicht bestaetigt ist. */
export const istOffen = (z: StundenZeile) => !!z.confirm_status && z.confirm_status !== "bestaetigt";

/**
 * Einsatz nachtragen — Inhaber wie Helfer.
 *
 * Vorher fuehrte der einzige Weg ueber die Planung: Termin anlegen, Slot
 * dranhaengen. Inhaberstunden landeten deshalb oft gar nicht in den
 * Projektzahlen. Der Server haengt den Einsatz an den Termin des gewaehlten
 * Tages oder legt einen an.
 *
 * Der Personenschluessel traegt den Typ mit (`inhaber:<id>`), weil Inhaber
 * unter derselben Id auch in `team_member` stehen — eine blanke Id waere
 * mehrdeutig.
 */
export function NachtragForm({
  projektId,
  onFertig,
}: {
  projektId: string;
  onFertig: (fehler: string | null) => void;
}) {
  const { data: members = [] } = useTeamMembers();
  const { data: owners = [] } = useTeamOwners();
  const create = useCreateStunde(projektId);

  const helfer = useMemo(
    () => members.filter((m) => m.active !== false && m.role !== "inhaber"),
    [members],
  );
  const inhaber = useMemo(() => owners.filter((o) => o.active !== false), [owners]);

  const [personKey, setPersonKey] = useState("");
  const [datum, setDatum] = useState(heuteIso);
  const [von, setVon] = useState("08:00");
  const [bis, setBis] = useState("16:00");
  const [pause, setPause] = useState("0");
  const [lohn, setLohn] = useState("0");
  const [zusatz, setZusatz] = useState("0");
  const [note, setNote] = useState("");

  /** Satz aus dem Teamprofil vorbelegen — bei Inhabern bleibt er 0: ihre Zeit
   *  ist kein Aufwand, sie misst den Stundenlohn des Projekts. */
  const waehlen = (key: string) => {
    setPersonKey(key);
    const [typ, id] = key.split(":");
    setLohn(typ === "helfer" ? String(helfer.find((h) => h.id === id)?.hourly_rate ?? 0) : "0");
  };

  const [typ, personId] = personKey.split(":");
  const bereit = !!personId && !!datum && von < bis;

  const speichern = () => {
    if (!bereit) return;
    create.mutate(
      {
        person_id: personId,
        person_typ: typ === "inhaber" ? "inhaber" : "helfer",
        datum,
        von,
        bis,
        pause_stunden: zahl(pause) ?? 0,
        stundenlohn: zahl(lohn) ?? 0,
        zusatzkosten: zahl(zusatz) ?? 0,
        note: note.trim(),
      },
      {
        onSuccess: () => onFertig(null),
        onError: (e) => onFertig(e instanceof Error ? e.message : String(e)),
      },
    );
  };

  return (
    <div className="vg-nachtrag">
      <div className="vg-nachtrag-grid">
        <label className="is-breit">
          Person
          <select value={personKey} onChange={(e) => waehlen(e.target.value)}>
            <option value="">— wählen —</option>
            <optgroup label="Inhaber">
              {inhaber.map((o) => (
                <option key={o.id} value={`inhaber:${o.id}`}>{o.name}</option>
              ))}
            </optgroup>
            <optgroup label="Helfer">
              {helfer.map((m) => (
                <option key={m.id} value={`helfer:${m.id}`}>{m.name}</option>
              ))}
            </optgroup>
          </select>
        </label>
        <label>
          Tag
          <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
        </label>
        <label>
          Von
          <input type="time" value={von} onChange={(e) => setVon(e.target.value)} />
        </label>
        <label>
          Bis
          <input type="time" value={bis} onChange={(e) => setBis(e.target.value)} />
        </label>
        <label>
          Pause (h)
          <input value={pause} onChange={(e) => setPause(e.target.value)} inputMode="decimal" />
        </label>
        <label>
          €/h
          <input value={lohn} onChange={(e) => setLohn(e.target.value)} inputMode="decimal" />
        </label>
        <label>
          Zusatz (€)
          <input value={zusatz} onChange={(e) => setZusatz(e.target.value)} inputMode="decimal" />
        </label>
        <label className="is-breit">
          Notiz
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Was wurde gemacht?" />
        </label>
      </div>
      <div className="vg-nachtrag-actions">
        <span className="vg-nachtrag-hint">
          {von >= bis
            ? "„Bis\u201c muss nach „Von\u201c liegen."
            : "Der Einsatz hängt sich an den Termin des Tages — oder legt einen an."}
        </span>
        <button type="button" className="vg-btn vg-btn-ghost" onClick={() => onFertig(null)}>
          Abbrechen
        </button>
        <button
          type="button"
          className="vg-btn vg-btn-primary"
          disabled={!bereit || create.isPending}
          onClick={speichern}
        >
          {create.isPending ? "Speichert…" : "Eintragen"}
        </button>
      </div>
    </div>
  );
}


/**
 * Kosten einer Stundenzeile — inklusive der Fälle, in denen 0 € richtig ist.
 *
 * Eine 0 ohne Erklärung liest sich wie ein Fehler — aber nicht jede 0 ist
 * einer. Hängt der Einsatz an einer Pauschale, steht das Geld dort (und würde
 * hier doppelt zählen). Unbezahlte Einsätze sind ebenfalls zulässig: Inhaber
 * arbeiten ohne Satz, Praktikantinnen wie Anke bekommen keinen Lohn — beide
 * haben deshalb 0 € im Teamprofil stehen.
 *
 * Gewarnt wird nur, wenn das Profil einen Satz kennt, die Zeile ihn aber
 * verloren hat. Das ist der Fall, der auf einen Fehler hindeutet.
 */
export function KostenZelle({ zeile, profilSatz }: { zeile: StundenZeile; profilSatz: number }) {
  const pauschal = !!zeile.pauschale_id || zeile.verguetungsart === "pauschale";
  if (pauschal) {
    return (
      <span className="is-muted" title="Der Betrag steht bei den Pauschalen und zählt dort einmal.">
        über Pauschale
        {zeile.zusatzkosten ? ` + ${formatEur(zeile.zusatzkosten, { round: true })}` : ""}
      </span>
    );
  }
  const satzVerloren = zeile.kosten === 0 && zeile.stunden_netto > 0
    && zeile.stundenlohn === 0 && zeile.person_typ !== "inhaber" && profilSatz > 0;
  if (satzVerloren) {
    return (
      <span title={`Im Teamprofil stehen ${formatEur(profilSatz, { round: true })} pro Stunde, an diesem Einsatz aber 0 €.`}>
        {formatEur(0, { round: true })}
        <span className="vg-proj-tag is-prio-hoch" style={{ marginLeft: 6 }}>Satz fehlt</span>
      </span>
    );
  }
  return <>{formatEur(zeile.kosten, { round: true })}</>;
}

export function StundenForm({
  zeile,
  busy,
  onAbbrechen,
  onSpeichern,
}: {
  zeile: StundenZeile;
  busy: boolean;
  onAbbrechen: () => void;
  onSpeichern: (patch: Record<string, unknown>) => void;
}) {
  const [von, setVon] = useState(uhrzeit(zeile.start_at));
  const [bis, setBis] = useState(uhrzeit(zeile.end_at));
  const [pause, setPause] = useState(String(zeile.pause_stunden || 0));
  const [lohn, setLohn] = useState(String(zeile.stundenlohn || 0));
  const [zusatz, setZusatz] = useState(String(zeile.zusatzkosten || 0));
  const [note, setNote] = useState(zeile.note || "");

  const bauen = () => {
    const patch: Record<string, unknown> = { note };
    const s = mitUhrzeit(zeile.start_at, von);
    const e = mitUhrzeit(zeile.end_at, bis);
    if (s) patch.start_at = s;
    if (e) patch.end_at = e;
    const p = zahl(pause);
    if (p != null) patch.pause_stunden = p;
    const l = zahl(lohn);
    if (l != null) patch.stundenlohn = l;
    const zk = zahl(zusatz);
    if (zk != null) patch.zusatzkosten = zk;
    return patch;
  };

  return (
    // Die Tabelle hat sechs Spalten; die acht Eingaben passen dort nicht
    // nebeneinander, ohne sie wieder breit zu machen. Sie stehen deshalb in
    // EINER Zelle als umbrechendes Feld — beschriftet, weil ein nacktes
    // Eingabefeld im Bearbeitungsmodus nichts ueber sich verraet.
    <tr className="vg-tabelle-form">
      <td>{zeile.datum || "—"}</td>
      <td>{zeile.person_name || "—"}</td>
      <td colSpan={3}>
        <div className="vg-form-felder">
          <label><span>Von</span><input type="time" value={von} onChange={(e) => setVon(e.target.value)} /></label>
          <label><span>Bis</span><input type="time" value={bis} onChange={(e) => setBis(e.target.value)} /></label>
          <label><span>Pause</span><input value={pause} onChange={(e) => setPause(e.target.value)} inputMode="decimal" className="is-schmal" /></label>
          <label><span>€/h</span><input value={lohn} onChange={(e) => setLohn(e.target.value)} inputMode="decimal" className="is-schmal" /></label>
          <label><span>Zusatz</span><input value={zusatz} onChange={(e) => setZusatz(e.target.value)} inputMode="decimal" className="is-schmal" /></label>
          <label className="is-weit"><span>Notiz</span><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" /></label>
          <span className="vg-form-summe">{h1Zahl(zeile.stunden_netto)} h netto</span>
        </div>
      </td>
      <td className="is-aktion">
        <button type="button" className="vg-icon-btn is-ok" title="Speichern" disabled={busy} onClick={() => onSpeichern(bauen())}>
          ✓
        </button>
        <button type="button" className="vg-icon-btn" title="Abbrechen" onClick={onAbbrechen}>
          ✕
        </button>
      </td>
    </tr>
  );
}
